import type { ReactNode } from 'react';
import { z } from 'zod';
import type { ProfileTemplate, TemplateId } from '@/api/network/types';
import type { CompanyListItem } from '@/api/questionnaires/types';
import type { BulkActionDef } from '@/components/shared/DataTable/BulkActions';
import type {
  SortingState,
  ToolbarActionDef,
} from '@/components/shared/DataTable/types';
import { fallback } from '@/lib/utils/zodFallback';

// ─── Re-exports from src/api/types for backwards compatibility ────────────────

export type { CompanyProfile } from '@/api/companies/types';
export type {
  CompanySearchPage,
  CompanySearchResult,
  DataPointSearchResult,
  EnrichedRiskFactor,
  EsgFieldType,
  EsgTemplateMetadata,
  GraphLayer,
  GraphLink,
  GraphProductionProcess,
  ImpactArea,
  NetworkMapParams,
  PatchAnswerPayload,
  PatchTemplatePayload,
  ProcessedRiskScores,
  ProfileTemplate,
  RiskFactor,
  RiskGroup,
  RiskMetricResult,
  TemplateId,
} from '@/api/network/types';
export type { GraphCompany, GraphNode } from '@/api/product-mapping/types';
export type {
  CompanyListItem,
  MyNetworkParams,
  MyNetworkResponse,
} from '@/api/questionnaires/types';
export type {
  AssessmentBlueprint,
  AssessmentBlueprintsResponse,
  RiskHistoryEntry,
  RiskLevelInfo as RiskLevel,
  RiskPrioritizationMeta,
  RiskPrioritizationResponse,
  RiskScores,
} from '@/api/risk/types';
export type { DataPoint, SlcpVerificationMeta } from '@/api/slcp/types';
export type { ConnectionType } from '@/api/supply-chain-mapping/types';
export type * from './profileHistory';

/** The General Info tab only ever renders these 3 templates — Webshop
 *  Profile is a separate tab/flow with its own `TemplateId`-typed patching. */
export type GeneralInfoTemplateId = Exclude<TemplateId, 'WEBSHOP_PROFILE'>;

// ─── Route search schemas ─────────────────────────────────────────────────────

/** /network/my-network — the main table/map/graph view of the user's network */
export const networkListSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^[\w]+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  search: z.string().optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
  view: fallback(z.enum(['table', 'map']), 'table'),
});

export type NetworkListSearch = z.infer<typeof networkListSearch>;

// ─── Field mapping ────────────────────────────────────────────────────────────

export type FieldMapping = {
  key: string;
  toApiValue: (raw: unknown, row?: CompanyListItem) => unknown;
  toLocalValue: (raw: unknown) => unknown;
};

// ─── Filter option types (kept here — route-local) ────────────────────────────

export interface FilterOption {
  key: string;
  label: string;
}

export interface CollaborationStatus {
  id: string;
  name: string;
}

export interface RelationshipType {
  id: string;
  name: string;
}

export interface DropdownUser {
  id: string;
  firstName: string;
  lastName: string;
}

export interface DropdownCompany {
  id: string;
  name: string;
  officialName?: string | null;
}

// ─── Profile tabs (General Info + ESG) — cross-template search ────────────────

/** One template that has search hits, ready to render as its own section. */
export interface ProfileSearchSection<TId extends string = string> {
  templateId: TId;
  templateName: string;
  template: ProfileTemplate;
  /** Visible data-point keys of this template that matched the query. */
  searchIds: Set<string>;
}

/** ESG template ids are dynamic, not a fixed union. */
export type EsgSearchSection = ProfileSearchSection;

/** General Info only ever renders its 3 fixed templates. */
export type GeneralInfoSearchSection =
  ProfileSearchSection<GeneralInfoTemplateId>;

/**
 * What a profile tab's content pane should render:
 * - `inactive` — no query, show the sidebar-selected template
 * - `loading`  — query typed, hits/templates still resolving
 * - `results`  — one section per template with hits
 * - `empty`    — query resolved with no matching field
 */
export type ProfileSearchState = 'inactive' | 'loading' | 'results' | 'empty';

// ─── Component prop types ─────────────────────────────────────────────────────

export interface NetworkTableProps {
  /**
   * Renders every cell read-only — set without `network:edit`. The inline
   * editors patch the company profile, which the API refuses for a reader, so
   * the table must not offer them.
   */
  readOnly?: boolean;
  data: CompanyListItem[];
  total: number;
  loading?: boolean;
  toolbarActions?: ToolbarActionDef[];
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (v: Record<string, boolean>) => void;
  sorting: SortingState;
  onSortingChange: (v: SortingState) => void;
  filters: Record<string, string[]>;
  onFiltersChange: (v: Record<string, string[]>) => void;
  pageIndex: number;
  pageSize: number;
  onPaginationChange: (v: { pageIndex: number; pageSize: number }) => void;
  onCellCommit: (
    rowIndex: number,
    colId: string,
    rawValue: unknown,
    row: CompanyListItem,
  ) => unknown;
  collaborationStatuses: FilterOption[];
  relationshipTypes: FilterOption[];
  facilityProcesses: FilterOption[];
  responsiblePersons: FilterOption[];
  networkCompanies: FilterOption[];
  officialNames: FilterOption[];
  /** Companies that are some network company's agent / vendor — the
   *  Agents / Vendors filter options. */
  agentOptions: FilterOption[];
  vendorOptions: FilterOption[];
  bulkActions?: BulkActionDef[];
  overlay?: ReactNode;
  /**
   * Amfori last-synced timestamps keyed by companyId. When provided (i.e. the
   * brand has connected amfori credentials) an "Amfori synced" column is shown.
   */
  amforiLastSyncedByCompanyId?: Record<string, string | null>;
}
